import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiServices } from '../api/services';
import { useAuth } from './useAuth';

/** Loads agreement acceptance separately for each active supplier organization. */
export function useSupplierTerms() {
  const { organization } = useAuth();
  const { organizations } = useApiServices();
  const isSupplier = organization?.type === 'supplier';
  const query = useQuery({
    queryKey: ['supplier-terms', organization?.id],
    queryFn: () => organizations.getSupplierTerms(),
    enabled: isSupplier,
    refetchOnMount: 'always',
  });
  return {
    ...query,
    isSupplier,
    hasAccepted:
      !isSupplier ||
      Boolean(
        query.data?.acceptance?.url === query.data?.currentUrl &&
        query.data?.acceptance?.acceptedAt,
      ),
  };
}

/** Saves acceptance and refreshes server-backed agreement and opportunity queries. */
export function useAcceptSupplierTerms() {
  const { organizations } = useApiServices();
  const { organization } = useAuth();
  const queryClient = useQueryClient();
  const organizationId = organization?.id;
  return useMutation({
    mutationFn: (url: string) => {
      if (!organizationId) throw new Error('No active organization');
      return organizations.acceptSupplierTerms(organizationId, url);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['supplier-terms', organizationId] });
      await Promise.all(
        [
          'supplier-rfqs',
          'supplier-rfq',
          'dashboard-metrics',
          'dashboard-activity',
          'nav-counts',
        ].map((key) => queryClient.invalidateQueries({ queryKey: [key] })),
      );
    },
  });
}
